import { Injectable } from '@angular/core';
import { createPopper, Instance as PopperInstance } from '@popperjs/core';

/**
 * Framework-agnostic positioning service using Popper.js
 * 
 * This service can be easily ported to React, Vue, or vanilla JS
 * by simply removing the @Injectable decorator.
 * 
 * Features:
 * - Automatic collision detection and flipping
 * - Viewport-aware positioning
 * - Auto-repositioning on scroll/resize
 * - WCAG 2.1 AA compliant (dismissible, keyboard accessible)
 * 
 * @example
 * // Usage in Angular
 * this.positioningService.openMenu(button, menu, () => this.closeMenu());
 * 
 * @example
 * // Future React version - same logic!
 * const service = new PopperService();
 * service.openMenu(buttonRef.current, menuRef.current, () => setOpen(false));
 */
@Injectable()
export class ChildTablePositioningService {
  private instances = new Map<
    string,
    {
      popperInstance: PopperInstance | null;
      menuElement: HTMLElement | null;
      triggerElement: HTMLElement | null;
      backdropClickHandler: ((e: MouseEvent) => void) | null;
      escapeKeyHandler: ((e: KeyboardEvent) => void) | null;
    }
  >();

  /**
   * Open menu with automatic positioning
   * 
   * @param triggerElement - Button that triggered the menu
   * @param menuElement - Menu element to position
   * @param onClose - Callback when menu closes
   */
  openMenu(triggerElement: HTMLElement, menuElement: HTMLElement, onClose: () => void): void;
  openMenu(menuKey: string, triggerElement: HTMLElement, menuElement: HTMLElement, onClose: () => void): void;
  openMenu(
    arg1: string | HTMLElement,
    arg2: HTMLElement,
    arg3: HTMLElement | (() => void),
    arg4?: () => void
  ): void {
    const menuKey = typeof arg1 === 'string' ? arg1 : 'default';
    const triggerElement = (typeof arg1 === 'string' ? arg2 : arg1) as HTMLElement;
    const menuElement = (typeof arg1 === 'string' ? arg3 : arg2) as HTMLElement;
    const onClose = (typeof arg1 === 'string' ? arg4 : arg3) as () => void;

    this.closeMenu(menuKey); // Clean up any existing instance for this key

    const instance = {
      popperInstance: null as PopperInstance | null,
      menuElement,
      triggerElement,
      backdropClickHandler: null as ((e: MouseEvent) => void) | null,
      escapeKeyHandler: null as ((e: KeyboardEvent) => void) | null,
    };
    this.instances.set(menuKey, instance);

    // Create Popper instance with smart positioning
    instance.popperInstance = createPopper(triggerElement, menuElement, {
      placement: 'bottom-start',
      strategy: 'fixed', // Use fixed positioning to escape overflow containers
      modifiers: [
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
            padding: 16,
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 16, // Keep 16px from viewport edges
            boundary: 'viewport',
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 4], // 4px gap from trigger
          },
        },
        {
          name: 'computeStyles',
          options: {
            // Ensure proper GPU acceleration
            gpuAcceleration: true,
          },
        },
      ],
    });

    // Show menu
    menuElement.style.display = 'block';
    
    // Update position after display (ensures correct dimensions)
    requestAnimationFrame(() => {
      instance.popperInstance?.update();
    });

    // Setup backdrop click handler (WCAG 1.4.13: dismissible)
    instance.backdropClickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!menuElement.contains(target) && !triggerElement.contains(target)) {
        onClose();
      }
    };
    
    // Delay to avoid immediate close from the triggering click
    setTimeout(() => {
      document.addEventListener('click', instance.backdropClickHandler!, { capture: true });
    }, 0);

    // Setup escape key handler (WCAG 2.1.2: no keyboard trap)
    instance.escapeKeyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', instance.escapeKeyHandler, { capture: true });
  }

  /**
   * Close menu and clean up all resources
   */
  closeMenu(): void;
  closeMenu(menuKey: string): void;
  closeMenu(menuKey?: string): void {
    if (typeof menuKey === 'string') {
      this.closeMenuByKey(menuKey);
      return;
    }

    // Close all menus
    for (const key of Array.from(this.instances.keys())) {
      this.closeMenuByKey(key);
    }
  }

  private closeMenuByKey(menuKey: string): void {
    const instance = this.instances.get(menuKey);
    if (!instance) return;

    // Destroy Popper instance
    if (instance.popperInstance) {
      instance.popperInstance.destroy();
      instance.popperInstance = null;
    }

    // Hide menu
    if (instance.menuElement) {
      instance.menuElement.style.display = 'none';
      instance.menuElement = null;
    }

    // Remove event listeners
    if (instance.backdropClickHandler) {
      document.removeEventListener('click', instance.backdropClickHandler, { capture: true });
      instance.backdropClickHandler = null;
    }

    if (instance.escapeKeyHandler) {
      document.removeEventListener('keydown', instance.escapeKeyHandler, { capture: true });
      instance.escapeKeyHandler = null;
    }

    instance.triggerElement = null;
    this.instances.delete(menuKey);
  }

  /**
   * Clean up on service destroy
   */
  destroy(): void {
    this.closeMenu();
  }
}
